/**
 * TASK-833 — what a signal's subject IS, independent of who is rendering it.
 *
 * This was two halves in two files: the shape lived on `SignalInvestigation` (the in-place
 * frame this ticket retires) and the mapping lived on `ChangeRadar`. The dossier is a canvas
 * tile that resolves its own subject, so the pair moved here rather than being imported out
 * of a component the tile has nothing else to do with.
 *
 * Nothing was rewritten in the move. `buildSignalSubject` is `buildInvestigationContext` with
 * its name changed to match what it now builds.
 */

import type { ActionPolicy } from '../../types/dashboard';
import type { ImpactedClient, RadarEvent } from '../../services/radarService';
import { namesAClient } from '../../utils/clientDisplayName';

/** The trusted subject a dossier opens on. Every field comes from the Radar payload. */
export interface SignalSubject {
  /** TASK-729 durable subject identity — what work binds to and what deep links address. */
  subjectKey: string;
  /** TASK-747 Radar group the subject was presented in. */
  signalGroupKey?: string | null;
  /** Where the case was opened FROM, so the surface can say so. */
  originType: 'change_radar' | 'alerts' | 'digest';
  originId?: string | null;
  sourceEvent: string;
  /** Display label — never used for identity (TASK-747). */
  source: string;
  action: string;
  entityKey?: string | null;
  entityLabel?: string | null;
  clientId: string;
  clientName?: string | null;
  exposureChf?: number | null;
  exposurePct?: number | null;
  /** Threshold / before / after, when the source has them (drift). */
  thresholdPct?: number | null;
  driftCaused?: number | null;
  /** When the alert row was written. NOT the age of the breach — see `SignalUrgency`. */
  detectedAt?: string | null;
  /** TASK-822: when the OBLIGATION falls due, and the derived state over it. */
  dueAt?: string | null;
  slaState?: string | null;
  /** The positions behind the exposure figure (TASK-747). */
  affectedPositionIds?: string[];
  /** Every contributing alert; the primary is the navigation target. */
  primaryAlertId?: string | null;
  alertIds?: string[];
  recommendedAction?: string | null;
  evidence?: string | null;
  /** TASK-731: categorical urgency of a mandatory compliance condition. */
  mandatory?: boolean;
  slaClass?: string | null;
  slaBreached?: boolean;
  /** TASK-731: exposure is NOT KNOWN — never render a figure or a bar for this subject. */
  exposureNotApplicable?: boolean;
  /** TASK-732: the server-owned action contract the footer renders from. */
  actionPolicy?: ActionPolicy | null;
}

/** The portfolio tab each source family's evidence lives on. */
export const TAB_FOR_SOURCE: Record<string, string> = {
  portfolio_drift_breach: 'composition',
  // TASK-831: a CIO SELL flip's evidence is the HOUSE VIEW, which now exists as a tab.
  cio_stale_sell: 'cio',
  dna_fit_conflict: 'personalization',
  kyc_staged_pending: 'overview',
  news_exposure: 'composition',
  // TASK-731: compliance conditions are client matters — their evidence is the client
  // record, not a portfolio tab. (Navigation targets, not action policy — policy is
  // server-owned and arrives on the payload.)
  screening_potential_match: 'overview',
  kyc_expired: 'overview',
  reprofile_due: 'overview',
  review_action_required: 'overview',
};

/** TASK-731: the compliance condition source events sharing one evidence body. */
export const COMPLIANCE_CONDITION_EVENTS = new Set([
  'screening_potential_match', 'kyc_expired', 'reprofile_due', 'review_action_required',
]);

/**
 * TASK-829 AC5 — which ROUTE holds a given tab, so a deep link never writes a param its
 * destination silently discards.
 *
 * TWO different pages render "a client", and each validates `?tab=` against its OWN list and
 * falls back to its default when the value is not in it — without saying so:
 *
 * - `/portfolio/client/:clientId` → `ClientLevelView` (the portfolio tabs, incl. `cio` and
 *   `personalization`, which TASK-829/831 made routable)
 * - `/clients/:clientId`          → `ClientDetail` (the client record)
 *
 * `AlertGroupCard` built every destination as `/clients/{id}?tab=…` and so sent `cio` and
 * `personalization` to the page that has neither: the advisor landed on Timeline, which is
 * the generic client tab this epic exists to remove (TASK-816, finding 2). `SignalDossier`
 * already guarded against it with a local copy of the allow-list; the pair lives here now so
 * the two surfaces cannot drift apart on "where does this signal's evidence live".
 */
export const CLIENT_LEVEL_TABS = new Set([
  'overview', 'composition', 'performance', 'risk', 'history', 'portfolios', 'personalization',
  'cio',
]);

/**
 * `ClientDetail`'s own tabs (`ClientDetail.tsx:117`), plus the two legacy values it redirects
 * to `compliance` — `kyc` and `risk-profile` are what the KYC and re-profile causes send, and
 * they resolve correctly, so they belong on this list rather than being "fixed".
 */
export const CLIENT_DETAIL_TABS = new Set([
  'timeline', 'documents', 'compliance', 'notes', 'insights', 'portfolios', 'dna', 'voice',
  'actions', 'kyc', 'risk-profile',
]);

/**
 * The href for a client's evidence on a given tab. The TAB chooses the route; a tab neither
 * page has yields a bare client link rather than a param that will be thrown away — "lands on
 * that tab or is not offered".
 *
 * `portfolios` is the one value both pages accept; the client record wins, which is what
 * `/clients/{id}?tab=portfolios` already resolved to before this helper existed.
 */
export function clientEvidenceHref(
  clientId: string,
  tab?: string | null,
  extraParams = '',
): string {
  if (tab && CLIENT_DETAIL_TABS.has(tab)) {
    return `/clients/${clientId}?tab=${tab}${extraParams}`;
  }
  if (tab && CLIENT_LEVEL_TABS.has(tab)) {
    return `/portfolio/client/${clientId}?tab=${tab}${extraParams}`;
  }
  return `/clients/${clientId}`;
}

/**
 * Compose the trusted subject a dossier opens on. Every field comes from the Radar payload
 * the advisor is already looking at; nothing is recomputed and nothing is invented — a field
 * the payload does not carry is simply omitted, and the surface renders it as explicitly
 * unavailable (TASK-722).
 */
export function buildSignalSubject(e: RadarEvent, c: ImpactedClient): SignalSubject | null {
  if (!c.subject_key) return null;   // no durable identity → nothing to open ON
  return {
    subjectKey: c.subject_key,
    signalGroupKey: e.group_key ?? null,
    originType: 'change_radar',
    originId: e.id,
    sourceEvent: e.source_event ?? '',
    source: e.source ?? '',
    action: e.action ?? '',
    entityKey: e.entity_key,
    entityLabel: e.entity_label,
    clientId: c.client_id,
    // TASK-841: guarded HERE rather than at each of the dossier's three render sites,
    // because `clientName` flows from this one place into the header, the escalate title and
    // the task title — and a durable task title with a UUID baked into it outlives every fix
    // to the surface that produced it.
    clientName: namesAClient(c.client_name, c.client_id) ? c.client_name : null,
    exposureChf: c.exposure_chf,
    exposurePct: c.exposure_pct,
    driftCaused: c.drift_caused,
    detectedAt: e.event_ts,
    dueAt: e.due_at ?? null,
    slaState: e.sla_state ?? null,
    affectedPositionIds: c.affected_position_ids ?? [],
    primaryAlertId: c.primary_alert_id ?? c.alert_id,
    alertIds: c.alert_ids,
    recommendedAction: c.suggested_action,
    evidence: c.dna_note,
    // TASK-731/732: the mandatory band and the server-owned action contract travel with
    // the subject, so the surface renders urgency and actions without any local rule table.
    mandatory: e.mandatory ?? false,
    slaClass: e.sla_class ?? null,
    slaBreached: e.sla_breached ?? false,
    exposureNotApplicable: e.exposure_not_applicable ?? false,
    actionPolicy: e.action_policy ?? null,
  };
}

/** Resolve a subject key against a Radar payload. Null means "not in this payload". */
export function findSubject(events: RadarEvent[], subjectKey: string): SignalSubject | null {
  for (const e of events) {
    const client = e.impacted_clients.find((c) => c.subject_key === subjectKey);
    if (client) return buildSignalSubject(e, client);
  }
  return null;
}
